import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaExclamationTriangle, FaFileAlt, FaCode, FaTable } from 'react-icons/fa';
import AssistantService from '../../services/AssistantService';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useAssistantChat } from './AssistantChatContext';
import ArtifactPanel from './ArtifactPanel';

const SUGGESTIONS = [
  '¿Qué documentos vencen esta semana?',
  '¿Qué le falta a Juan Pérez?',
  '¿Cuántos empleados hay activos?',
];

// Traduce los ids de herramienta del backend a texto legible para el pie de la burbuja.
const TOOL_LABELS = {
  listar_empleados: 'consultó personas',
  buscar_persona_por_documento: 'buscó por documento',
  datos_de_persona: 'consultó una persona',
  documentos_por_vencer: 'consultó vencimientos',
  inspecciones_por_vencer: 'consultó inspecciones',
  listar_obras: 'consultó obras',
  listar_capacitaciones: 'consultó capacitaciones',
  proponer_crear_inspeccion: 'preparó una inspección',
  proponer_crear_obra: 'preparó una obra',
  crear_documento: 'preparó un documento',
};

const ARTIFACT_KIND = { markdown: 'Documento', html: 'Vista HTML', texto: 'Texto', tabla: 'Tabla' };

const ERROR_COPY = {
  unconfigured: 'El asistente todavía no está disponible. Avisale al administrador.',
  limit: 'Se alcanzó el límite de uso por ahora. Probá de nuevo más tarde.',
  network: 'No pudimos conectar. Revisá tu conexión e intentá de nuevo.',
  auth: 'Tu sesión expiró. Iniciá sesión de nuevo.',
  unknown: 'Ocurrió un error. Intentá de nuevo.',
};

// Techo de contexto: aviso al acercarse (aprox por caracteres, ~4 chars/token).
const TOKEN_LIMIT = 30000;
const WARN_AT = 24000;
const estimateTokens = (msgs) =>
  Math.round(
    msgs.reduce((n, m) => n + (m.content || m.proposal?.resumen || '').length, 0) / 4,
  );

const modelLabel = (model) => {
  const m = (model || '').toLowerCase();
  if (m.includes('haiku')) return 'Haiku';
  if (m.includes('sonnet')) return 'Sonnet';
  return model || '';
};

const footerText = (model, toolsUsed) => {
  const label = modelLabel(model);
  const tools = [...new Set((toolsUsed || []).map((t) => TOOL_LABELS[t] || t))];
  return tools.length ? `${label} · ${tools.join(', ')}` : label;
};

const bubbleBase = {
  padding: '8px 12px',
  marginBottom: '8px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
const userBubble = {
  ...bubbleBase,
  marginLeft: 'auto',
  maxWidth: '80%',
  background: '#0d6efd',
  color: '#fff',
  borderRadius: '14px 14px 4px 14px',
};
const assistantBubble = {
  ...bubbleBase,
  maxWidth: '85%',
  background: '#f1f3f5',
  color: '#212529',
  borderRadius: '14px 14px 14px 4px',
};
const chipStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  maxWidth: '85%',
  textAlign: 'left',
  padding: '8px 12px',
  border: '1px solid #cfe2ff',
  background: '#fff',
  borderRadius: '12px',
  cursor: 'pointer',
};

const matchesDesktop = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(min-width: 992px)').matches
    : true;

function AssistantChat() {
  // Estado del chat en contexto: sobrevive el cambio de módulo (en memoria, no en disco).
  const { messages, setMessages, highReasoning, setHighReasoning, reset } =
    useAssistantChat();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [openArtifact, setOpenArtifact] = useState(null);
  const [isDesktop, setIsDesktop] = useState(matchesDesktop);
  const bodyRef = useRef(null);

  const usedTokens = estimateTokens(messages);
  const nearLimit = usedTokens >= WARN_AT;
  const allArtifacts = messages.flatMap((m) => m.artifacts || []);
  const showPanel = !!openArtifact;

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 992px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const send = async (textArg) => {
    const text = (typeof textArg === 'string' ? textArg : input).trim();
    if (!text || loading) return;

    // Los turnos previos (solo texto), acotados para no inflar tokens.
    const history = messages
      .filter((m) => m.type === 'text')
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, { role: 'user', type: 'text', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const result = await AssistantService.sendMessage({ message: text, history, highReasoning });
      setMessages((prev) => {
        const next = [
          ...prev,
          {
            role: 'assistant',
            type: 'text',
            content: result.reply,
            model: result.model,
            toolsUsed: result.toolsUsed,
            artifacts: result.artifacts || [],
          },
        ];
        // Fase 2: cada propuesta se muestra como una tarjeta de acción a confirmar.
        (result.pendingActions || []).forEach((proposal) => {
          next.push({ role: 'assistant', type: 'action', proposal, status: 'pending' });
        });
        return next;
      });
      // Si generó documentos, abrimos el último en el panel.
      if (result.artifacts && result.artifacts.length > 0) {
        setOpenArtifact(result.artifacts[result.artifacts.length - 1]);
      }
    } catch (err) {
      // La pregunta queda en el hilo; el error se muestra en snackbar.
      setSnackbar({ open: true, type: 'error', message: ERROR_COPY[err?.kind] || ERROR_COPY.unknown });
    } finally {
      setLoading(false);
    }
  };

  const setStatus = (index, status) =>
    setMessages((prev) => prev.map((m, i) => (i === index ? { ...m, status } : m)));

  const confirmAction = async (index) => {
    const msg = messages[index];
    if (!msg || msg.type !== 'action' || msg.status !== 'pending') return;
    setStatus(index, 'confirming');
    try {
      await AssistantService.executeAction(msg.proposal);
      setStatus(index, 'confirmed');
      const okMsg =
        msg.proposal?.action === 'enviar_email'
          ? 'Email enviado correctamente'
          : 'Creado correctamente';
      setSnackbar({ open: true, type: 'success', message: okMsg });
    } catch (err) {
      setStatus(index, 'pending');
      setSnackbar({ open: true, type: 'error', message: err?.message || ERROR_COPY.unknown });
    }
  };

  const cancelAction = (index) => setStatus(index, 'cancelled');

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const nuevoChat = () => {
    reset();
    setInput('');
    setOpenArtifact(null);
  };

  const renderMessage = (m, i) => {
    switch (m.type) {
      case 'action':
        return (
          <div key={i} style={{ maxWidth: '85%', marginRight: 'auto', marginBottom: '8px' }}>
            <Card border="primary">
              <Card.Body className="py-2 px-3">
                <div className="mb-2">{m.proposal.resumen}</div>
                {m.status === 'pending' && (
                  <div className="d-flex justify-content-end gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => cancelAction(i)}>
                      Cancelar
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => confirmAction(i)}>
                      Confirmar
                    </Button>
                  </div>
                )}
                {m.status === 'confirming' && (
                  <div className="text-muted small">
                    {m.proposal?.action === 'enviar_email' ? 'Enviando…' : 'Creando…'}
                  </div>
                )}
                {m.status === 'confirmed' && (
                  <div className="text-success small">
                    {m.proposal?.action === 'enviar_email' ? '✓ Enviado' : '✓ Confirmada'}
                  </div>
                )}
                {m.status === 'cancelled' && <div className="text-muted small">Cancelada</div>}
              </Card.Body>
            </Card>
          </div>
        );
      case 'text':
      default:
        return (
          <div key={i} style={m.role === 'user' ? userBubble : assistantBubble}>
            {m.content}
            {m.role === 'assistant' && m.artifacts && m.artifacts.length > 0 && (
              <div className="d-grid gap-1 mt-2">
                {m.artifacts.map((a) => (
                  <button key={a.id} type="button" style={chipStyle} onClick={() => setOpenArtifact(a)}>
                    {a.type === 'tabla' ? <FaTable /> : a.type === 'html' ? <FaCode /> : <FaFileAlt />}
                    <span>
                      <span className="fw-semibold">{a.title}</span>
                      <span className="text-muted small d-block">
                        {ARTIFACT_KIND[a.type] || 'Documento'} · Abrir →
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {m.role === 'assistant' && footerText(m.model, m.toolsUsed) && (
              <div className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                {footerText(m.model, m.toolsUsed)}
              </div>
            )}
          </div>
        );
    }
  };

  const artifactPanel = (
    <ArtifactPanel
      artifact={openArtifact}
      allArtifacts={allArtifacts}
      onSelect={setOpenArtifact}
      onClose={() => setOpenArtifact(null)}
      onCopied={() =>
        setSnackbar({ open: true, type: 'success', message: 'Copiado al portapapeles' })
      }
    />
  );

  const chatCard = (
    <Card style={{ height: 'calc(100vh - 130px)', display: 'flex', flexDirection: 'column' }}>
      <Card.Header
        className="bg-primary text-white"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <div>
          <h5 className="m-0">Asistente</h5>
          <small style={{ opacity: 0.85 }}>
            Preguntá sobre personas, documentos, vencimientos, obras e inspecciones.
          </small>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '12px', flexWrap: 'wrap' }}>
          {messages.length > 0 && (
            <Button variant="outline-light" size="sm" onClick={nuevoChat} disabled={loading}>
              Nuevo chat
            </Button>
          )}
          <div>
            <Form.Check
              type="switch"
              id="alto-razonamiento"
              label="Alto razonamiento"
              checked={highReasoning}
              onChange={(e) => setHighReasoning(e.target.checked)}
              disabled={loading}
            />
            {highReasoning && (
              <small className="text-warning d-flex align-items-center" style={{ gap: '4px' }}>
                <FaExclamationTriangle /> Consume más tokens. Usalo para preguntas complejas.
              </small>
            )}
          </div>
        </div>
      </Card.Header>

      <Card.Body ref={bodyRef} style={{ flex: 1, overflowY: 'auto' }}>
        {messages.length === 0 && !loading ? (
          <div className="text-center d-flex flex-column align-items-center justify-content-center h-100">
            <h6>Preguntale al asistente</h6>
            <p className="text-muted" style={{ maxWidth: '420px' }}>
              Puede consultar personas, documentos, vencimientos, obras e inspecciones.
            </p>
            <div className="d-grid gap-2" style={{ maxWidth: '420px', width: '100%' }}>
              {SUGGESTIONS.map((s) => (
                <Button key={s} variant="outline-secondary" size="sm" onClick={() => send(s)}>
                  {s}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}
            {loading && (
              <div style={assistantBubble}>
                {highReasoning ? 'Pensando (alto razonamiento)…' : 'Pensando…'}
              </div>
            )}
          </>
        )}
      </Card.Body>

      <Card.Footer>
        {nearLimit && (
          <Alert variant="warning" className="py-1 px-2 mb-2 small">
            El chat se está llenando (~{Math.round(usedTokens / 1000)}k / {TOKEN_LIMIT / 1000}k
            tokens). Reiniciá con «Nuevo chat» para limpiar el contexto.
          </Alert>
        )}
        <Form onSubmit={handleSubmit} className="d-flex gap-2">
          <Form.Control
            type="text"
            placeholder="Escribí tu pregunta…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            autoFocus
          />
          <Button type="submit" variant="primary" disabled={loading || !input.trim()}>
            {loading ? <Spinner size="sm" animation="border" /> : 'Enviar'}
          </Button>
        </Form>
      </Card.Footer>
    </Card>
  );

  return (
    <Container fluid className="mt-3" style={{ maxWidth: showPanel && isDesktop ? '100%' : '900px' }}>
      <Row className="g-3">
        <Col xs={12} lg={showPanel && isDesktop ? 5 : 12} xl={showPanel && isDesktop ? 4 : 12}>
          {chatCard}
        </Col>
        {showPanel && isDesktop && (
          <Col>
            <div style={{ height: 'calc(100vh - 130px)' }}>{artifactPanel}</div>
          </Col>
        )}
      </Row>

      {!isDesktop && (
        <Modal show={showPanel} fullscreen onHide={() => setOpenArtifact(null)}>
          <Modal.Body className="p-0" style={{ height: '100%' }}>
            {showPanel && artifactPanel}
          </Modal.Body>
        </Modal>
      )}

      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Container>
  );
}

export default AssistantChat;
